import React, { useState, useEffect } from 'react';
import { parseServicePackageFeatures } from '../../lib/formatters';

function PackageModal({ package: pkg, isOpen, onClose, onSave, isNewPackage = false }) {
  const [formData, setFormData] = useState({
    package_name: '',
    package_tier: 'Bronze',
    display_name: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    features: ['']
  });

  useEffect(() => {
    if (pkg) {
      const list = parseServicePackageFeatures(pkg.features);
      const features = list.length ? list : [''];
      setFormData({
        package_name: pkg.package_name || '',
        package_tier: pkg.package_tier || 'Bronze',
        display_name: pkg.display_name || '',
        description: pkg.description || '',
        price_monthly: pkg.price_monthly || '',
        price_yearly: pkg.price_yearly || '',
        features: features
      });
    } else {
      setFormData({
        package_name: '',
        package_tier: 'Bronze',
        display_name: '',
        description: '',
        price_monthly: '',
        price_yearly: '',
        features: ['']
      });
    }
  }, [pkg]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const filteredFeatures = formData.features.filter((f) => f.trim() !== '');
    const packageData = {
      ...formData,
      /* SQLite stores TEXT — must send JSON string or DB saves "[object Object]" */
      features: JSON.stringify({ features: filteredFeatures }),
    };

    onSave(packageData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content package-modal">
        <div className="modal-header">
          <h2>{isNewPackage ? 'Add New Package' : 'Edit Package'}</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-section">
            <h3>📋 Package Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="package_name">Package Name *</label>
                <input
                  type="text"
                  id="package_name"
                  name="package_name"
                  value={formData.package_name}
                  onChange={handleInputChange}
                  placeholder="e.g., bronze-starter"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="package_tier">Package Tier *</label>
                <select
                  id="package_tier"
                  name="package_tier"
                  value={formData.package_tier}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Bronze">Bronze</option>
                  <option value="Silver">Silver</option>
                  <option value="Gold">Gold</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="display_name">Display Name *</label>
              <input
                type="text"
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                placeholder="e.g., Bronze Starter Package"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Package description..."
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>💰 Pricing</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price_monthly">Monthly Price ($)</label>
                <input
                  type="number"
                  id="price_monthly"
                  name="price_monthly"
                  value={formData.price_monthly}
                  onChange={handleInputChange}
                  placeholder="99.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="price_yearly">Yearly prepaid ($)</label>
                <input
                  type="number"
                  id="price_yearly"
                  name="price_yearly"
                  value={formData.price_yearly}
                  onChange={handleInputChange}
                  placeholder="990.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.45 }}>
              Reference (per <code>OPTIPRIME_BUSINESS_MODEL_SUMMARY.md</code> v2.0):
              Entry (Bronze) <strong>$25/mo → $255/yr</strong> · Professional (Silver) <strong>$75/mo → $743/yr</strong> ·
              High-Volume Professional (Gold) <strong>$200/mo → $2040/yr</strong>.
              Annual discount range: ~<strong>15 – 17.5 %</strong> off monthly.
            </p>
          </div>

          <div className="form-section">
            <h3>✨ Features</h3>
            {formData.features.map((feature, index) => (
              <div key={index} className="feature-input-group">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  placeholder="Enter feature..."
                  className="feature-input"
                />
                {formData.features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="btn btn-sm btn-danger"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addFeature}
              className="btn btn-sm btn-secondary"
            >
              <i className="fas fa-plus"></i> Add Feature
            </button>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isNewPackage ? 'Create Package' : 'Update Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PackageModal;
